import { logger } from "@-/dev.improve.haniverse.lib.logger";
import { createMMKV } from "@-/com.npmjs.react-native-mmkv";
import { createSyncStorage, prefixStorage, type Storage } from "@-/com.npmjs.unstorage-sync";
import { mmkvDriver } from "@-/com.npmjs.unstorage-sync-react-native-mmkv";
import {
  deviceSettingsSchemas,
  type DeviceSettingKey,
  type DeviceSettings,
} from "#device-settings-schemas";

const root: Storage = createSyncStorage({
  driver: mmkvDriver({ createMMKV, id: "device-settings" }),
});

export const deviceSettingsStorage: Storage = prefixStorage(root, "device");
export const deviceAccountStorage: Storage = prefixStorage(root, "device-account");

// Memoizes the last parsed snapshot per key, keyed by the raw stored value.
// `useSyncExternalStore` requires `getSnapshot` to return a referentially-stable
// value when nothing changed; an object-valued schema (e.g. `preferredAudioInput`)
// parses to a fresh object every read, which would compare unequal forever and
// spin an infinite render loop. Caching by the serialized raw value gives every
// unchanged read the same reference.
const snapshotCache = new Map<DeviceSettingKey, { raw: string; value: unknown }>();

export function getDeviceSetting<K extends DeviceSettingKey>(key: K): DeviceSettings[K] {
  const raw = deviceSettingsStorage.getItem(key);
  const rawKey = JSON.stringify(raw ?? null);
  const cached = snapshotCache.get(key);
  if (cached !== undefined && cached.raw === rawKey) {
    // oxlint-disable-next-line typescript/no-unsafe-type-assertion -- cached under this key, so it's DeviceSettings[K]
    return cached.value as DeviceSettings[K];
  }
  const parsed = deviceSettingsSchemas[key].safeParse(raw ?? undefined);
  if (parsed.success) {
    snapshotCache.set(key, { raw: rawKey, value: parsed.data });
    // oxlint-disable-next-line typescript/no-unsafe-type-assertion -- schema key correlates with DeviceSettings[K]
    return parsed.data as DeviceSettings[K];
  }
  logger(import.meta.location).warn(
    `device-settings: stored value for "${key}" failed validation, resetting to default`,
    parsed.error,
  );
  // oxlint-disable-next-line typescript/no-unsafe-type-assertion -- schema key correlates with DeviceSettings[K]
  const fallback = deviceSettingsSchemas[key].parse(undefined) as DeviceSettings[K];
  // oxlint-disable-next-line typescript/no-unsafe-type-assertion -- unstorage sync setItem is typed as never for generic storage
  deviceSettingsStorage.setItem(key, fallback as never);
  snapshotCache.set(key, { raw: JSON.stringify(fallback ?? null), value: fallback });
  return fallback;
}

export function setDeviceSetting<K extends DeviceSettingKey>(
  key: K,
  value: DeviceSettings[K],
): void {
  const validated = deviceSettingsSchemas[key].parse(value);
  // oxlint-disable-next-line typescript/no-unsafe-type-assertion -- unstorage sync setItem is typed as never for generic storage
  deviceSettingsStorage.setItem(key, validated as never);
}

/**
 * Wipes all persisted device settings and the stored device account, returning
 * everything to its defaults. Used by the admin "Reset data" control so the
 * device-local state is reset alongside the databases.
 */
export function resetDeviceSettings(): void {
  // prefixStorage().clear() passes a base key that getMounts() never matches, so
  // clear the shared MMKV root instead.
  root.clear();
  // Drop memoized snapshots so the next read re-parses from the now-empty store
  // (and yields fresh default references) rather than returning stale values.
  snapshotCache.clear();
}
